import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
// UPPDRAG-8 PR-E: dual-write result confirm/dispute system messages.
import { sendLeagueMatchMessage } from "@/lib/chatDualWrite";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

interface ConfirmResultProps {
  match: Record<string, unknown>;
  teamId: string;
  league: Record<string, unknown>;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ConfirmResult({ match, teamId: _teamId, league: _league, onClose, onSuccess }: ConfirmResultProps) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const result = match.result as Record<string, unknown> | null;

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("league_matches")
        .update({
          confirmed_by: user!.id,
          confirmed_at: new Date().toISOString(),
        })
        .eq("id", match.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user!.id)
          .single();
        const name = profile?.full_name || "Okänd";
        // UPPDRAG-8 PR-E: dual-write + structured metadata.
        await sendLeagueMatchMessage(supabase, {
          matchId: match.id,
          senderUserId: user!.id,
          body: `Resultat bekräftat av ${name}`,
          isSystem: true,
          data: {
            type: "result_confirmed",
            confirmer_name: name,
            confirmer_user_id: user!.id,
          },
        });
      } catch {
        // Non-critical
      }
      toast({ title: "Resultat bekräftat!" });
      queryClient.invalidateQueries({ queryKey: ["league-matches"] });
      queryClient.invalidateQueries({ queryKey: ["match-detail", match.id] });
      queryClient.invalidateQueries({ queryKey: ["division-standings"] });
      queryClient.invalidateQueries({ queryKey: ["match-chat", match.id] });
      onSuccess();
    },
    onError: (err: Error) => {
      toast({ title: "Fel", description: err.message, variant: "destructive" });
    },
  });

  const disputeMutation = useMutation({
    mutationFn: async () => {
      // Save dispute status on the match
      const { error } = await supabase
        .from("league_matches")
        .update({
          disputed_by: user!.id,
          disputed_at: new Date().toISOString(),
          dispute_resolved: false,
        })
        .eq("id", match.id);
      if (error) throw error;

      // System chat message
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user!.id)
          .single();
        const name = profile?.full_name || "Okänd";
        // UPPDRAG-8 PR-E: dual-write + structured metadata.
        await sendLeagueMatchMessage(supabase, {
          matchId: match.id,
          senderUserId: user!.id,
          body: `⚠️ Resultat bestrids av ${name}. Admin avgör.`,
          isSystem: true,
          data: {
            type: "result_disputed",
            disputer_name: name,
            disputer_user_id: user!.id,
          },
        });
      } catch {
        // Non-critical
      }

      // Notify admins
      try {
        if (organization) {
          const { data: adminRoles } = await supabase
            .from("user_roles")
            .select("user_id")
            .eq("role", "admin")
            .eq("organization_id", organization.id);
          if (adminRoles && adminRoles.length > 0) {
            await supabase.from("notifications").insert(
              adminRoles.map((r) => ({
                user_id: r.user_id,
                organization_id: organization.id,  // PR-B: org-scope per Phase 1 RLS (gated by `if (organization)` above)
                title: "Resultat bestrids",
                body: `Ett matchresultat har bestridits och behöver avgöras.`,
                type: "match_disputed",
                data: { match_id: match.id },
              }))
            );
          }
        }
      } catch {
        // Non-critical
      }
    },
    onSuccess: () => {
      toast({ title: "Resultat bestrids", description: "Admin har meddelats och kommer avgöra tvisten." });
      queryClient.invalidateQueries({ queryKey: ["match-detail", match.id] });
      queryClient.invalidateQueries({ queryKey: ["match-chat", match.id] });
      queryClient.invalidateQueries({ queryKey: ["admin-league-matches"] });
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: "Fel", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Bekräfta resultat</h3>

      {result?.sets && (
        <div className="flex justify-center gap-4">
          {result.sets.map((set: Record<string, unknown>, i: number) => (
            <div key={i} className="text-center">
              <p className="text-xs text-muted-foreground">Set {i + 1}</p>
              <p className="text-lg font-bold">
                {set.home}-{set.away}
              </p>
              {set.tiebreak && (
                <p className="text-xs text-muted-foreground">
                  TB: {set.tiebreak_home}-{set.tiebreak_away}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {result?.points_home !== undefined && (
        <div className="text-center text-sm">
          <p className="text-muted-foreground">Poäng</p>
          <p className="text-lg font-bold">
            {result.points_home} – {result.points_away}
          </p>
        </div>
      )}

      <Separator />

      {match.reported_at && (
        <p className="text-xs text-muted-foreground text-center">
          Rapporterat {format(new Date(match.reported_at), "d MMM HH:mm", { locale: sv })}
        </p>
      )}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onClose}>
          Avbryt
        </Button>
        <Button
          variant="destructive"
          className="flex-1"
          disabled={disputeMutation.isPending}
          onClick={() => disputeMutation.mutate()}
        >
          {disputeMutation.isPending ? "Bestrider..." : "Bestrid"}
        </Button>
        <Button
          className="flex-1"
          disabled={confirmMutation.isPending}
          onClick={() => confirmMutation.mutate()}
        >
          {confirmMutation.isPending ? "Bekräftar..." : "Bekräfta"}
        </Button>
      </div>
    </div>
  );
}
